import React, { useEffect, useState } from 'react';
import { t } from 'i18next';
import BahlouLogoFull from '@/assets/Bahlou_logo.png';
import { RadixButton, RadixCard, RadixOtpInput, RadixSpinner } from '@/components/radix';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { completeEmployeeSetupApi, validateEmployeeSetupTokenApi } from '@/utils/Api/AuthV3';
import { SetupValidateTokenData, SetupValidateTokenResponse } from '@/utils/Api/AuthV3/authV3.types';
import { toast } from 'sonner';

const ACCESS_CODE_LENGTH = 6;

interface EmployeeSetupFormValues {
    name: string;
    email: string;
    accessCode: string[];
}

export default function EmployeeSetupIndex() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [isValidatingToken, setIsValidatingToken] = useState(true);
    const [apiResponse, setApiResponse] = useState<SetupValidateTokenData | null>(null);

    const [initialValues, setInitialValues] = useState<EmployeeSetupFormValues>({
        name: '',
        email: '',
        accessCode: Array(ACCESS_CODE_LENGTH).fill(''),
    });

    const validationSchema = Yup.object().shape({
        accessCode: Yup.array()
            .of(Yup.string().required(''))
            .min(ACCESS_CODE_LENGTH, t('Setting.MustBeExactly6Digits')),
    });

    const formik = useFormik<EmployeeSetupFormValues>({
        initialValues,
        enableReinitialize: true,
        validationSchema,
        onSubmit: async (values) => {
            const token = searchParams.get('token');
            if (!token) return;

            const code = values.accessCode.join('');
            try {
                const response = await completeEmployeeSetupApi({
                    token,
                    access_code: code,
                    access_code_confirm: code,
                });
                const accessToken = response?.data?.access_token;
                if (!accessToken) return;

                localStorage.setItem('auth_token', accessToken);
                localStorage.setItem('employee_id', response?.data?.employee?.id?.toString() ?? '');
                localStorage.setItem('role', response?.data?.employee?.role ?? '');
                setIsValidatingToken(false);
                formik.resetForm();
                navigate('/calendar', { replace: true });
            } catch (error: any) {
                toast.error(
                    error?.response?.data?.detail ??
                        error?.response?.data?.msg ??
                        error?.response?.data?.message ??
                        'something went wrong',
                );
                setIsValidatingToken(false);
                formik.resetForm();
                console.error(error);
                navigate('/', { replace: true });
            }
        },
    });

    useEffect(() => {
        const token = searchParams.get('token');
        if (!token) {
            navigate('/', { replace: true });
            return;
        }

        const validateToken = async () => {
            try {
                const response: SetupValidateTokenResponse = await validateEmployeeSetupTokenApi(token);
                const data = response?.data;
                if (!data?.valid) {
                    navigate('/', { replace: true });
                    return;
                }
                setApiResponse(data);
                setInitialValues((prev) => ({
                    ...prev,
                    name: data?.employee?.name ?? data?.employee_name ?? '',
                    email: data?.employee?.email ?? data?.store_details?.email ?? '',
                }));
            } catch (error) {
                navigate('/', { replace: true });
            } finally {
                setIsValidatingToken(false);
            }
        };
        validateToken();
    }, [navigate, searchParams]);

    if (isValidatingToken) {
        return (
            <div className="h-screen w-full flex items-center justify-center bg-[#FDFCFB]">
                <RadixSpinner size="lg" />
            </div>
        );
    }

    const displayEmployeeName = formik.values.name
        .split(' ')
        .map((n) => n.charAt(0))
        .slice(0, 2)
        .join('');

    return (
        <div className="relative min-h-screen sm:h-screen w-full flex flex-col items-center justify-center bg-[#FDFCFB] overflow-hidden px-4 py-6">
            {/* Background Decorations - Hidden on small mobile to save GPU/Cleanliness */}
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-primary-500/10 blur-3xl hidden sm:block" />
            <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-primary-500/10 blur-3xl hidden sm:block" />

            <div className="z-10 w-full max-w-[480px] flex flex-col h-full sm:h-auto justify-center">
                {/* Scaled Logo for Mobile */}
                <div className="flex justify-center mb-6 sm:mb-10">
                    <img src={BahlouLogoFull} alt="Bahlou" className="h-8 sm:h-12 object-contain" />
                </div>

                <RadixCard className="!p-0 border !border-[#E6E1D9] shadow-xl rounded-md bg-white/95 backdrop-blur-md overflow-hidden">
                    {/* Header Section - More Compact */}
                    <div className="bg-white pt-8 text-center border-b border-[#F0EDE8]">
                        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-primary-500 text-white rounded-full flex items-center justify-center text-xl sm:text-2xl font-bold mx-auto mb-3 shadow-lg rotate-2">
                            <span className="-rotate-2 uppercase">{displayEmployeeName || 'B'}</span>
                        </div>
                        <h1 className="text-lg sm:text-2xl font-bold text-gray-900 leading-tight">
                            {t('Setting.EmployeeInviteFlowTitle')}, <br className="sm:hidden" />
                            <span className="text-primary-500">{formik.values.name}</span>
                        </h1>
                        <p className="text-gray-500 text-xs sm:text-sm mt-1 font-medium">{apiResponse?.outlet_name}</p>
                    </div>

                    <div className="p-5 sm:p-8">
                        {/* Email Badge - Smaller for mobile */}
                        <div className="flex justify-center mb-6">
                            <span className="flex items-center gap-2 px-3 py-1 bg-[#F5F3F0] rounded-full border border-[#E6E1D9] max-w-full overflow-hidden">
                                <EmailIcon className="w-3 h-3 text-primary-500 flex-shrink-0" />
                                <span className="text-[10px] sm:text-xs font-semibold text-gray-600 truncate">
                                    {formik.values.email}
                                </span>
                            </span>
                        </div>

                        <div className="text-center mb-6">
                            <h3 className="text-gray-800 text-sm sm:text-base font-bold mb-1">Enter Access Code</h3>
                            <p className="text-gray-500 text-[11px] sm:text-sm leading-snug px-2">
                                {t('Setting.EmployeeInviteAccessCodeDescription', { employeeName: formik.values.name })}
                            </p>
                        </div>

                        {/* Input Grid - Fixed height/width for better mobile fit */}
                        <div className="mb-4">
                            <RadixOtpInput
                                id="employee-setup-access-code"
                                name="accessCode"
                                autoComplete="one-time-code"
                                value={formik.values.accessCode.join('')}
                                length={ACCESS_CODE_LENGTH}
                                fitContainer
                                onChange={(value) => {
                                    const digits = value.replace(/\D/g, '').slice(0, ACCESS_CODE_LENGTH).split('');
                                    const padded = Array.from(
                                        { length: ACCESS_CODE_LENGTH },
                                        (_, index) => digits[index] || '',
                                    );
                                    void formik.setFieldValue('accessCode', padded);
                                }}
                                inputClassName="w-10 h-16 sm:w-14 text-center text-xl sm:text-2xl font-bold bg-transparent border-2 border-solid border-[#E6E1D9] rounded-lg sm:rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-500/10 transition-all outline-none"
                            />
                        </div>

                        <RadixButton
                            variant="primary"
                            className="w-full !h-12 sm:!h-14 !rounded-xl !text-sm sm:!text-base font-bold mt-2 sm:mt-4 text-white"
                            onClick={() => formik.handleSubmit()}
                            disabled={formik.isSubmitting}
                        >
                            {formik.isSubmitting ? <RadixSpinner size="sm" /> : t('Common.Continue')}
                        </RadixButton>
                    </div>
                </RadixCard>
            </div>
        </div>
    );
}

const EmailIcon = ({ className }: { className?: string }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        className={className}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
        <polyline points="22,6 12,13 2,6" />
    </svg>
);
